import React from "react"

// ** Third Party Components
import { he } from "date-fns/locale"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import { formatDate, formatDistance } from "date-fns"
import heLocale from "@fullcalendar/core/locales/he"
import enGbLocale from "@fullcalendar/core/locales/en-gb"
import interactionPlugin from "@fullcalendar/interaction" // needed for dayClick

// ** Types
import { Task } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Theme Components
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

interface TaskProps {
  title: string
  start: string // Task start date and time
  end: string
  color: string
}

interface GanttProps {
  tasks: Task[] | [] | undefined
}
const TaskFC: React.FC<TaskProps> = ({ title, start, end, color }) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  return (
    <Tooltip>
      <TooltipTrigger
        className={cn(
          "w-full truncate text-ellipsis rounded-md p-1 text-xs text-black",
          !color && "bg-primary",
        )}
        style={{
          background: color,
        }}
      >
        {title}
      </TooltipTrigger>
      <TooltipContent className='z-50 bg-primary'>
        {translate("Expected finish date")}
        {start && end
          ? " " +
            translate("in") +
            " " +
            formatDistance(end, start, {
              locale: isHebrew ? he : undefined,
            })
          : translate("on") +
            " " +
            formatDate(start, "MMM d,yyy", {
              locale: isHebrew ? he : undefined,
            })}
      </TooltipContent>
    </Tooltip>
  )
}

export const TaskCalendar: React.FunctionComponent<GanttProps> = ({ tasks }) => {
  const { locale } = useTranslator()
  const fullCalendarLocale = locale === "he" ? heLocale : enGbLocale // or 'en' as fallback

  return (
    <div className='h-fit w-full'>
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView='dayGridMonth'
        locale={fullCalendarLocale}
        events={
          tasks?.map((task, index) => {
            return {
              id: index,
              title: task.title,
              start: new Date(task.start_at),
              end: new Date(task.finish_to),
              backgroundColor: task?.status?.color,
            }
          }) as any[]
        }
        eventContent={(eventInfo) => {
          return (
            <TaskFC
              title={eventInfo.event.title}
              start={eventInfo.event.startStr}
              end={eventInfo.event.endStr}
              color={eventInfo.backgroundColor}
            />
          )
        }}
      />
    </div>
  )
}
